import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import {
  Box, Card, Typography, TextField, MenuItem, InputAdornment,
  IconButton, Tooltip, Button, TableContainer, Paper, Table,
  TableHead, TableRow, TableCell, TableBody, TablePagination,
  CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CommonLayerUpload from '../CertificationManager/CommonLayerUpload';
import FitmentUpload from '../CertificationManager/FitmentUpload';
import UpdateRequestDialog from '../CertificationManager/UpdateRequestDialog';
// import AssignToL1, { AssignToL1Data } from '../CertificationManager/AssignToL1';
import * as XLSX from 'xlsx';
import { useLazyQuery, useMutation } from '@apollo/client';
import { GET_ALL_REQUESTS, UPLOAD_PDF_MUTATION, UPLOAD_CERTIFICATE_MUTATION } from './queries';


type Certificate = {
  id: number;
  kyc_id?: string;
  request_id: string;
  vin: string;
  project_name: string;
  device_model: string;
  device_serial_number: string;
  device_imei_number: string;
  engine_number: string;
  iccid_number: string;
  customer_name: string;
  customer_number: string;
  customer_otp_number: string;
  customer_address: string;
  vehicle_model: string;
  registration_state: string;
  rto_name: string;
  rto_number: string;
  dealer_name: string;
  dealer_contact_number: string;
  alternate_dealer_name: string;
  alternate_dealer_number: string;
  sim_activation_status: string;
  msisdn_1: string;
  msisdn_2: string;
  vehicle_type: string;
  year_of_manufacture: number;
  comman_layer_cert: string | null;
  common_layer_cert_issued_date: number | null;
  fitment_cert: string | null;
  fitment_cert_issued_date: number | null;
  commonLayerUpload: string;
  fitmentUpload: string;
  commonLayerDownload: string;
  // common_layer_cert_issued_date: string;
  // fitment_cert_issued_date: string;
  vaahanIssuedOn: string;
  inProgressDate: string;
  paymentStatus: string;
  requestStatus: string;
  otp: string;
  otpSentOn: string;
  reason: string;
  attachments: string;
  telematics: string;
  assignToL1: string;
  category: string;
  subCategory: string;
};



const TicketManager = () => {
  const [searchType, setSearchType] = useState('vin');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [isExporting, setIsExporting] = useState(false);

  const { data: queryData, loading, refetch } = useQuery(GET_ALL_REQUESTS, {
    variables: {
      offset: page * rowsPerPage,
      limit: rowsPerPage,
      searchType: searchType,
      searchTerm: searchTerm.trim()
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const handleSearchTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchType(e.target.value);
    setPage(0);
  };

  const handleSearchTermChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };

  const totalCount = queryData?.getAllRequests?.count ?? 0;

  const tableData: Certificate[] = (queryData?.getAllRequests?.data ?? []).map(
    (item: any, index: number) => ({
      id: index + 1 + page * rowsPerPage,
      kyc_id: item.kyc_id || '',
      request_id: item.request_id || '',
      vin: item.vin || '',
      project_name: item.project_name || '',
      device_model: item.device_model || '',
      device_serial_number: item.device_serial_number || '',
      device_imei_number: item.device_imei_number || '',
      engine_number: item.engine_number || '',
      iccid_number: item.iccid_number || '',
      customer_name: item.customer_name || '',
      customer_number: item.customer_number || '',
      customer_otp_number: item.customer_otp_number || '',
      customer_address: item.customer_address || '',
      vehicle_model: item.vehicle_model || '',
      registration_state: item.registration_state || '',
      rto_name: item.rto_name || '',
      rto_number: item.rto_number || '',
      dealer_name: item.dealer_name || '',
      dealer_contact_number: item.dealer_contact_number || '',
      alternate_dealer_name: item.alternate_dealer_name || '',
      alternate_dealer_number: item.alternate_dealer_number || '',
      sim_activation_status: item.sim_activation_status || '',
      msisdn_1: item.msisdn_1 || '',
      msisdn_2: item.msisdn_2 || '',
      vehicle_type: item.vehicle_type || '',
      year_of_manufacture: item.year_of_manufacture || 0,
      comman_layer_cert: item.comman_layer_cert || null,
      //common_layer_cert_issued_date: item.common_layer_cert_issued_date || null,
      fitment_cert: item.fitment_cert || null,
      // fitment_cert_issued_date: item.fitment_cert_issued_date || null,
      commonLayerUpload: '',
      fitmentUpload: '',
      commonLayerDownload: 'N/A',
      common_layer_cert_issued_date: item.common_layer_cert_issued_date || 'N/A',
      fitment_cert_issued_date: item.fitment_cert_issued_date || 'N/A',
      vaahanIssuedOn: 'N/A',
      inProgressDate: '',
      paymentStatus: '',
      requestStatus: item.status || '',
      otp: '',
      otpSentOn: '',
      reason: item.rejection_reason || '',
      attachments: 'N/A',
      telematics: 'N/A',
      assignToL1: '',
      category: '',
      subCategory: '',
    })
  );
  const [commonLayerDialogOpen, setCommonLayerDialogOpen] = useState(false);
  const [fitmentDialogOpen, setFitmentDialogOpen] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState<Certificate | null>(null);
  const [assignToL1DialogOpen, setAssignToL1DialogOpen] = useState(false);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);

  const [getAllRequests, { data: requestsData }] = useLazyQuery(GET_ALL_REQUESTS);

  const [uploadPdf] = useMutation(UPLOAD_PDF_MUTATION);
  const [uploadCertificate] = useMutation(UPLOAD_CERTIFICATE_MUTATION);

  const handleUploadPdf = async (
    file: File
  ): Promise<{ success: boolean; url?: string; error?: string }> => {
    try {
      const result = await uploadPdf({ variables: { pdf: file } });
      return result.data.uploadPdf;
    } catch (error: any) {
      console.error('Error uploading PDF:', error);
      return { success: false, error: error.message };
    }
  };

  const handleUploadCertificate = async (input: {
    url: string;
    kyc_id: string;
    document_type: string;
  }): Promise<{ success: boolean; message?: string; error?: string }> => {
    try {
      const result = await uploadCertificate({ variables: { input } });
      return result.data.uploadCertificate;
    } catch (error: any) {
      console.error('Error uploading certificate:', error);
      return { success: false, error: error.message };
    }
  };

  const tableColumns = [
    'Sr.No', 'Request ID', 'VIN', 'Common Layer Upload', 'CLA Issued On', 'Fitment Upload', 'Vaahan Issued On', 'Common Layer Download',
    'Project Name',
    'InProgress Date', 'Device Model', 'Device Serial Number', 'Device IMEI Number',
    'Engine Number', 'ICCID Number', 'Customer Name', 'Customer Number',
    'Customer OTP number', 'Customer Address', 'Vehicle Model', 'Registration State',
    'RTO Name', 'RTO Number', 'Dealer Name', 'Dealer Contact Number', 'Alternate Dealer Name',
    'Alternate Dealer Number', 'Payment Status', 'SIM Activation status', 'MSISDN1',
    'MSISDN2', 'Vehicle Type', 'Year Of Manufacture', 'Request Status', 'OTP',
    'OTP Sent On', 'Reason', 'Attachments', 'Telematics', 'Assign To L1', 'Category', 'Sub-Category'
  ];

  const cellStyle = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    padding: { xs: '4px', sm: '6px' },
    fontSize: { xs: '0.75rem', sm: '0.85rem' },
    borderRight: '1px solid #e0e0e0',
  };

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
    refetch({ offset: newPage * rowsPerPage, limit: rowsPerPage });
  };

  interface ChangeRowsPerPageEvent extends React.ChangeEvent<HTMLInputElement> { }

  const handleChangeRowsPerPage = (event: ChangeRowsPerPageEvent) => {
    const rpp = parseInt(event.target.value, 10);
    setRowsPerPage(rpp);
    setPage(0);
    refetch({ offset: 0, limit: rpp });
  };

const handleExportToExcel = async () => {
    if (totalCount === 0) {
      alert("No data available to export");
      return;
    }

    try {
      setIsExporting(true);
      const { data: allDataResponse } = await getAllRequests({
        variables: {
          offset: 0,
          limit: totalCount, 
          searchType: searchType,
          searchTerm: searchTerm.trim()
        },
        fetchPolicy: 'network-only'
      });

      const rawRecords = allDataResponse?.getAllRequests?.data ?? [];
      const formattedData = rawRecords.map((item: any, index: number) => {

        return {
          'Sr.No': index + 1,
          'Request ID': item.request_id || "N/A",
          'VIN': item.vin || "N/A",
          'Project Name': item.project_name || "N/A",
          'Device Model': item.device_model || "N/A",
          'Device Serial Number': item.device_serial_number || "N/A",
          'Device IMEI Number': item.device_imei_number || "N/A",
          'Engine Number': item.engine_number || "N/A",
          'ICCID Number': item.iccid_number || "N/A",
          'Customer Name': item.customer_name || "N/A",
          'Customer Number': item.customer_number || "N/A",
          'Customer OTP number': item.customer_otp_number || "N/A",
          'Customer Address': item.customer_address || "N/A",
          'Vehicle Model': item.vehicle_model || "N/A",
          'Registration State': item.registration_state || "N/A",
          'RTO Name': item.rto_name || "N/A",
          'RTO Number': item.rto_number || "N/A",
          'Dealer Name': item.dealer_name || "N/A",
          'Dealer Contact Number': item.dealer_contact_number || "N/A",
          'Alternate Dealer Name': item.alternate_dealer_name || "N/A",
          'Alternate Dealer Number': item.alternate_dealer_number || "N/A",
          'Payment Status': "N/A", 
          'SIM Activation status': item.sim_activation_status || "N/A",
          'MSISDN1': item.msisdn_1 || "N/A",
          'MSISDN2': item.msisdn_2 || "N/A",
          'Vehicle Type': item.vehicle_type || "N/A",
          'Year Of Manufacture': item.year_of_manufacture || "N/A",
          'Request Status': item.status || "N/A",
          'OTP': "N/A",
          'OTP Sent On': "N/A",
          'Reason': item.rejection_reason || "N/A",
          'Attachments': "N/A",
          'Category': "N/A",
          'Sub-Category': "N/A",
          'CLA Issued On': item.common_layer_cert_issued_date || "N/A",
          'Fitment Issued On': item.fitment_cert_issued_date || "N/A",
          'Vaahan Issued On': "N/A",
        };
      });
      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'All Tickets');
      XLSX.writeFile(workbook, `tickets_all_${Date.now()}.xlsx`);

    } catch (error) {
      console.error("Failed to export all requests:", error);
      alert("An error occurred while exporting data.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleButtonClick = (column: string, certificate: Certificate) => {
    if (column === 'Assign To L1') {
      setSelectedCertificate(certificate);
      setAssignToL1DialogOpen(true);
    } else if (column === 'Telematics') {
      console.log('Telematics clicked', certificate.id);
    }
  };

  const handleAssignToL1DialogClose = () => {
    setAssignToL1DialogOpen(false);
    setSelectedCertificate(null);
  };

  //   const handleAssignToL1Submit = (data: AssignToL1Data) => {
  //     console.log('Assign to L1 submitted:', data, selectedCertificate);
  //     setAssignToL1DialogOpen(false);
  //     setSelectedCertificate(null);
  //   };

  const handleUploadClick = (column: string, certificate: Certificate) => {
    setSelectedCertificate(certificate);
    if (column === 'Common Layer Upload') {
      setCommonLayerDialogOpen(true);
    } else if (column === 'Fitment Upload') {
      setFitmentDialogOpen(true);
    }
  };

  const handleCommonLayerFileUpload = async (file: File) => {
    if (selectedCertificate) {
      try {
        console.log("File upload completed:", file.name);
        refetch();
      } catch (error) {
        console.error("Error handling common layer file upload:", error);
      }
    }
  };

  const handleFitmentFileUpload = async (file: File) => {
    if (selectedCertificate) {
      try {
        console.log("File upload completed:", file.name);
        refetch();
      } catch (error) {
        console.error("Error handling fitment file upload:", error);
      }
    }
  };

  const handleCloseCommonLayerDialog = () => {
    setCommonLayerDialogOpen(false);
    setSelectedCertificate(null);
  };

  const handleCloseFitmentDialog = () => {
    setFitmentDialogOpen(false);
    setSelectedCertificate(null);
  };

  // Set mock data
  // useEffect(() => {
  //   setLoading(false);
  //   const transformedData: Certificate[] = mockData.map((item, index) => ({
  //     id: index + 1,
  //     ...item,
  //     commonLayerUpload: '',
  //     fitmentUpload: '',
  //     commonLayerDownload: item.commonLayerDownload || "N/A",
  //     common_layer_cert_issued_date: item.common_layer_cert_issued_date || "N/A",
  //     fitment_cert_issued_date: item.fitment_cert_issued_date || "N/A",
  //     vaahanIssuedOn: item.vaahanIssuedOn || "N/A",
  //     inProgressDate: item.inProgressDate || "",
  //     paymentStatus: item.paymentStatus || "",
  //     requestStatus: item.requestStatus || "",
  //     otp: item.otp || "",
  //     otpSentOn: item.otpSentOn || "",
  //     reason: item.reason || "",
  //     attachments: item.attachments || "",
  //     telematics: item.telematics || "",
  //     assignToL1: "",
  //     category: item.category || "",
  //     subCategory: item.subCategory || "",
  //   }));
  //   setTableData(transformedData);
  //   setPaginationCount(transformedData.length);
  // }, []);

  // useEffect(() => {
  //   getAllRequests();
  //   if (requestsData) {
  //     setTableData(requestsData?.getAllRequests?.data?.data || []);
  //   }
  // }, [requestsData, getAllRequests]);

  const paginatedData = tableData;

  const handleUpdateDialogClose = () => {
    setUpdateDialogOpen(false);
    setSelectedCertificate(null);
  };

  const handleUpdateRequestSubmit = (formData: { otp: string; reason: string; status: string }) => {
    console.log("Update Request Data:", {
      requestId: selectedCertificate?.request_id,
      vin: selectedCertificate?.vin,
      ...formData,
    });
    setUpdateDialogOpen(false);
  };

  return (
    <Box sx={{ width: '100%', px: { xs: 0, sm: 0 } }}>
      <Typography variant="h5" fontSize={{ xs: '1.5rem', sm: '2.125rem' }} sx={{ mb: 2, borderRadius: 2 }}>
        Tickets
      </Typography>
      <Card sx={{
        width: '100%',
        maxWidth: '100vw',
        mx: 'auto',
        boxShadow: 2,
        borderRadius: 3,
        p: 0,
        display: 'flex',
        flexDirection: 'column',
        height: { xs: 'auto', md: 'calc(100vh - 120px)' },
        bgcolor: '#fff',
        minHeight: 100,
      }}>
        {/* Sticky header controls */}
        <Box sx={{
          position: 'sticky',
          top: 0,
          zIndex: 20,
          background: '#fff',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          boxShadow: '0 2px 8px -4px #e0e0e0',
          px: { xs: 1, sm: 2 },
          pt: 2,
          pb: 2,
          left: 0,
          right: 0,
          width: '100%',
          minWidth: 0,
          overflow: 'visible',
        }}>
          <Box
            display="flex"
            flexDirection={{ xs: 'column', sm: 'row' }}
            sx={{ justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}
          >
            <Box
              display="flex"
              gap={1}
              alignItems="center"
              sx={{ minWidth: 0, flex: 1, maxWidth: { xs: '100%', sm: '40%' }, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}
            >
              <TextField
                select
                size="small"
                value={searchType}
                onChange={handleSearchTypeChange}
                sx={{ minWidth: 120, background: '#f5f5f5', flex: '0 0 auto' }}
              >
                <MenuItem value="vin">Search by VIN</MenuItem>
                <MenuItem value="request_id">Search by Request ID</MenuItem>
              </TextField>

              <TextField
                size="small"
                placeholder={
                  searchType === 'vin' ? 'Enter VIN' :
                    searchType === 'request_id' ? 'Enter Request ID' : ''
                }
                variant="outlined"
                sx={{ minWidth: 120, background: '#f5f5f5', flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                value={searchTerm}
                onChange={handleSearchTermChange}
              />
              <IconButton loading={isExporting} sx={{ ml: 1, color: '#000', flex: '0 0 auto' }} onClick={handleExportToExcel} >
                <Tooltip title="Export to Excel" placement="top" arrow>
                  <CloudDownloadIcon />
                </Tooltip>
              </IconButton>
            </Box>
          </Box>
        </Box>

        {/* Table area */}
        <Box sx={{
          flex: 1,
          minHeight: 0,
          px: { xs: 0.5, sm: 2 },
          py: 2,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: 'none',
                borderRadius: 2,
                width: '100%',
                minWidth: 600,
                background: '#fff',
                overflow: 'visible',
              }}
            >
              <Table stickyHeader size="small" sx={{ minWidth: 600, width: '100%', tableLayout: 'auto' }}>
                <TableHead>
                  <TableRow sx={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#E60012' }}>
                    {tableColumns.map((header) => (
                      <TableCell
                        key={header}
                        align="center"
                        sx={{
                          backgroundColor: '#E60012',
                          color: '#fff',
                          fontWeight: 'bold',
                          fontSize: { xs: '0.75rem', sm: '0.85rem' },
                          whiteSpace: 'nowrap',
                          padding: { xs: '6px', sm: '8px' },
                          borderBottom: '1px solid #e0e0e0',
                          borderRight: '2px solid #e0e0e0',
                          boxShadow: '0 2px 8px -4px #e0e0e0',
                          position: 'sticky',
                          top: 0,
                          zIndex: 11,
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={tableColumns.length} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : (!paginatedData || paginatedData.length === 0) ? (
                    <TableRow>
                      <TableCell colSpan={tableColumns.length} align="center" sx={{ fontSize: '1.1rem', color: '#888', py: 4 }}>
                        No data available
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedData.map((certificate, idx) => (
                      <TableRow
                        key={certificate.id}
                        hover
                        sx={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f5f5f5' }}
                      >
                        <TableCell align="center" sx={cellStyle}>{page * rowsPerPage + idx + 1}</TableCell>
                        <TableCell
                          align="center"
                          sx={cellStyle}
                          onClick={() => {
                            setSelectedCertificate(certificate);
                            setUpdateDialogOpen(true);
                          }}
                          style={{ cursor: "pointer", color: "green", textDecoration: "underline" }}
                        >
                          {certificate.request_id || "N/A"}
                        </TableCell>

                        <TableCell align="center" sx={cellStyle}>{certificate.vin || "N/A"}</TableCell>
                        {/* Common Layer Upload button */}
                        <TableCell align="center" sx={cellStyle}>
                          <Button
                            variant="contained"
                            onClick={() => handleUploadClick('Common Layer Upload', certificate)}
                            disabled={!!certificate.comman_layer_cert}
                            sx={{
                              backgroundColor: !!certificate.comman_layer_cert ? '#f0f0f0' : '#58B80F1A',
                              color: !!certificate.comman_layer_cert ? '#888' : '#58B80F',
                              borderRadius: '20px',
                              height: '24px',
                              padding: '0 12px',
                              fontSize: '12px',
                              fontWeight: 'normal',
                              textTransform: 'none',
                              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                              '&:hover': {
                                backgroundColor: !!certificate.comman_layer_cert ? '#f0f0f0' : '#58B80F33',
                              },
                              border: `1px solid ${!!certificate.comman_layer_cert ? '#e0e0e0' : '#58B80F'}`,
                              minWidth: '50px',
                            }}
                          >
                            <CloudUploadIcon fontSize="small" />
                          </Button>
                        </TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.common_layer_cert_issued_date}</TableCell>
                        {/* Fitment Upload button */}
                        <TableCell align="center" sx={cellStyle}>
                          <Button
                            variant="contained"
                            onClick={() => handleUploadClick('Fitment Upload', certificate)}
                            disabled={!!certificate.fitment_cert}
                            sx={{
                              backgroundColor: !!certificate.fitment_cert ? '#f0f0f0' : '#58B80F1A',
                              color: !!certificate.fitment_cert ? '#888' : '#58B80F',
                              borderRadius: '20px',
                              height: '24px',
                              padding: '0 12px',
                              fontSize: '12px',
                              fontWeight: 'normal',
                              textTransform: 'none',
                              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                              '&:hover': {
                                backgroundColor: !!certificate.fitment_cert ? '#f0f0f0' : '#58B80F33',
                              },
                              border: `1px solid ${!!certificate.fitment_cert ? '#e0e0e0' : '#58B80F'}`,
                              minWidth: '50px',
                            }}
                          >
                            <CloudUploadIcon fontSize="small" />
                          </Button>
                        </TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.vaahanIssuedOn}</TableCell>
                        <TableCell align="center" sx={cellStyle}>
                          {certificate.comman_layer_cert ? (
                            <Tooltip title="Download Common Layer Certificate">
                              <IconButton href={certificate.comman_layer_cert} target="_blank" rel="noopener noreferrer">
                                <CloudDownloadIcon color="primary" />
                              </IconButton>
                            </Tooltip>
                          ) : "N/A"}
                        </TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.project_name || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.inProgressDate || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.device_model || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.device_serial_number || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.device_imei_number || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.engine_number || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.iccid_number || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.customer_name || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.customer_number || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.customer_otp_number || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.customer_address || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.vehicle_model || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.registration_state || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.rto_name || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.rto_number || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.dealer_name || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.dealer_contact_number || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.alternate_dealer_name || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.alternate_dealer_number || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.paymentStatus || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.sim_activation_status || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.msisdn_1 || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.msisdn_2 || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.vehicle_type || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.year_of_manufacture || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.requestStatus || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.otp || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.otpSentOn || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.reason || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.attachments || "N/A"}</TableCell>
                        {/* Telematics column with button */}
                        <TableCell align="center" sx={cellStyle}>
                          <Button
                            variant="contained"
                            onClick={() => handleButtonClick('Telematics', certificate)}
                            sx={{
                              backgroundColor: '#58B80F1A',
                              color: '#58B80F',
                              borderRadius: '12px',
                              height: '24px',
                              padding: '0 12px',
                              fontSize: '12px',
                              fontWeight: 'normal',
                              textTransform: 'none',
                              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                              '&:hover': {
                                backgroundColor: '#58B80F33',
                              },
                              border: '1px solid #58B80F',
                              minWidth: '108px',
                            }}
                          >
                            TELEMATICS
                          </Button>
                        </TableCell>
                        {/* Assign To L1 column with button */}
                        <TableCell align="center" sx={cellStyle}>
                          <Button
                            variant="contained"
                            onClick={() => handleButtonClick('Assign To L1', certificate)}
                            sx={{
                              backgroundColor: '#58B80F1A',
                              color: '#58B80F',
                              borderRadius: '12px',
                              height: '24px',
                              padding: '0 12px',
                              fontSize: '12px',
                              fontWeight: 'normal',
                              textTransform: 'none',
                              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                              '&:hover': {
                                backgroundColor: '#58B80F33',
                              },
                              border: '1px solid #58B80F',
                              minWidth: '108px',
                            }}
                          >
                            ASSIGN
                          </Button>
                        </TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.category || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.subCategory || "N/A"}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>

        {/* Pagination */}
        <Box sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: 20,
          background: '#fff',
          borderTop: '1px solid #e0e0e0',
          pt: 2,
          mt: 'auto',
          overflowX: 'auto',
        }}>
          <TablePagination
            rowsPerPageOptions={[20, 50, 100, 500]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page:"
            sx={{
              minWidth: '600px',
              '.MuiTablePagination-toolbar': {
                flexWrap: 'nowrap',
                justifyContent: { xs: 'center', sm: 'space-between' },
              },
            }}
          />
        </Box>
      </Card>

      {/* Common Layer Upload Dialog */}
      <CommonLayerUpload
        open={commonLayerDialogOpen}
        onClose={handleCloseCommonLayerDialog}
        onUpload={handleCommonLayerFileUpload}
        uploadPdfMutation={handleUploadPdf}
        uploadCertificateMutation={handleUploadCertificate}
        kycId={selectedCertificate?.kyc_id || ''}
      />

      {/* Fitment Upload Dialog */}
      <FitmentUpload
        open={fitmentDialogOpen}
        onClose={handleCloseFitmentDialog}
        onUpload={handleFitmentFileUpload}
        uploadPdfMutation={handleUploadPdf}
        uploadCertificateMutation={handleUploadCertificate}
        kycId={selectedCertificate?.kyc_id || ''}
        certificate={selectedCertificate}
      />

      {/* Assign To L1 Dialog */}
      {/* <AssignToL1
        open={assignToL1DialogOpen}
        onClose={handleAssignToL1DialogClose}
        onSubmit={handleAssignToL1Submit}
        certificate={selectedCertificate}
      /> */}
      <UpdateRequestDialog
        open={updateDialogOpen}
        onClose={handleUpdateDialogClose}
        onSubmit={handleUpdateRequestSubmit}
        requestId={selectedCertificate?.request_id || ""}
        vin={selectedCertificate?.vin || ""}
      />

    </Box>
  );
};

export default TicketManager;